import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Animated, StyleSheet } from 'react-native';
import styled from 'styled-components/native';

import { PORTAL_GATE_ALERT } from '@webtrader/core/constants/portal';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTranslation } from '@webtrader/core/i18n';
import { NotificationType } from '@webtrader/core/services/alert';
import { useTheme } from '@webtrader/core/services/theme/v2';

import Ionicons from '@/components/Icons/Ionic';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';

import { removeGate } from '../Portal/PortalUtil';

const ABSOLUTE_MODAL_DURATION = 5;

interface IAbsoluteModalProps {
  type?: NotificationType;
  refresh: boolean;
  message: string;
  title?: string;
  id?: string;
  actionText?: string;
  noButton?: boolean;
  onClose?: () => void;
}

const AbsoluteModal: FC<IAbsoluteModalProps> = (props) => {
  const { type, refresh, message, title = '', id, noButton, onClose } = props;

  const { theme } = useTheme();
  const { t } = useTranslation();

  const [visible, setVisible] = useState<boolean>(true);
  const [needRefresh, setNeedRefresh] = useState<boolean>();
  const [duration, setDuration] = useState(ABSOLUTE_MODAL_DURATION);
  const [timer, setTimer] = useState<NodeJS.Timeout>();

  const [fadeAnimation] = useState(new Animated.Value(0));

  const onCloseClick = useCallback(() => {
    if (noButton) {
      return;
    }
    setVisible(false);
  }, [noButton]);

  const updateDuration = useCallback(
    (newDuration: number) => {
      clearInterval(timer);
      setDuration(newDuration);
    },
    [timer]
  );

  useEffect(() => {
    if (!visible) {
      Animated.timing(fadeAnimation, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true
      }).start();

      removeGate(PORTAL_GATE_ALERT);
      onClose?.();
    }
  }, [visible, fadeAnimation, onClose]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDuration((prevDuration) => prevDuration - 1);

      if (duration === 0) {
        clearInterval(intervalId);
        setVisible(false);
      }
    }, 1000);

    setTimer(intervalId);

    return () => {
      clearInterval(intervalId);
    };
  }, [duration, needRefresh]);

  useEffect(() => {
    Animated.timing(fadeAnimation, {
      toValue: 20,
      duration: 100,
      useNativeDriver: true
    }).start();

    if (needRefresh !== refresh) {
      setNeedRefresh(refresh);
      updateDuration(ABSOLUTE_MODAL_DURATION);
    }
  }, [needRefresh, refresh, fadeAnimation, updateDuration]);

  const displayIcon = useMemo(() => {
    switch (type) {
      case 'error':
        return <Ionicons name={'alert-circle'} color={theme.colors.tag.red} />;
      case 'info':
        return <Ionicons name={'information-circle-outline'} color={theme.colors.tag.blue} />;
      case 'success':
      default:
        return <Ionicons name={'checkmark-circle'} color={theme.colors.tag.green} />;
    }
  }, [type, theme]);

  const animatedStyle = {
    transform: [
      {
        translateY: fadeAnimation
      }
    ]
  };

  return (
    <StyledAlertContainer style={[styles.alertBox, animatedStyle]} testID={EDataTestId.NOTIFICATION_BOX}>
      <StyledIconContainer>{displayIcon}</StyledIconContainer>
      <StyledContentContainer>
        {!!title && <StyledContentTitle testID={EDataTestId.NOTIFICATION_BOX_TITLE}>{title}</StyledContentTitle>}
        {id && (
          <StyledContentId>
            {t('Order No.')} {id}
          </StyledContentId>
        )}
        <StyledContentDescContainer>
          <StyledContentDesc testID={EDataTestId.NOTIFICATION_BOX_DESCRIPTION}>{message}</StyledContentDesc>
        </StyledContentDescContainer>
      </StyledContentContainer>
      <TouchableOpacity onPress={onCloseClick} testID={EDataTestId.NOTIFICATION_BOX_CLOSE}>
        <StyledIconContainer>
          <Ionicons name={'close-outline'} color={theme.colors.text[2]} />
        </StyledIconContainer>
      </TouchableOpacity>
    </StyledAlertContainer>
  );
};

export default AbsoluteModal;

const styles = StyleSheet.create({
  alertBox: {
    shadowColor: 'rgb(129, 139, 139)',
    shadowOpacity: 0.5,
    shadowRadius: 4
  }
});

const StyledAlertContainer = styled(Animated.View)`
  display: flex;
  flex-direction: row;

  margin-top: ${dynamicSize.convertSize(50)}px;
  padding: ${dynamicSize.convertSize(16)}px ${dynamicSize.convertSize(12)}px;
  background-color: ${({ theme }) => theme.colors.background[0]};
  border-radius: ${dynamicSize.convertSize(3)}px;

  position: absolute;
  top: 0;
  left: ${dynamicSize.convertSize(16)}px;
  right: ${dynamicSize.convertSize(16)}px;
  z-index: 9999;
`;

const StyledIconContainer = styled.View`
  display: flex;
`;

const StyledContentContainer = styled.View`
  flex: 1;
  flex-direction: column;
  display: flex;
  margin: 0 ${dynamicSize.convertSize(12)}px;
`;

const StyledContentTitle = styled(Text)`
  font-size: ${dynamicSize.convertSize(14)}px;
  font-weight: 600;
  margin-bottom: ${dynamicSize.convertSize(4)}px;
`;

const StyledContentId = styled(Text)`
  font-size: ${dynamicSize.convertSize(12)}px;
  color: ${({ theme }) => theme.colors.text[1]};

  margin-top: ${dynamicSize.convertSize(4)}px;
`;

const StyledContentDescContainer = styled.View`
  flex-direction: row;
`;

const StyledContentDesc = styled(Text)`
  flex: 1;
  flex-wrap: wrap;
  font-size: ${dynamicSize.convertSize(14)}px;
`;
